/**
 * ChatComposer — tag chips + input + send for the project chat.
 *
 * Keeps D9's correct behaviours: Enter sends / Shift+Enter newlines, focus stays in the textarea
 * after sending, and tags are NOT cleared between turns (the common flow is several instructions
 * against the same files, and re-ticking chips every turn is pure friction).
 *
 * `focusToken` lets the parent pull focus here declaratively — used when the last file tab is closed
 * (the chat becomes the visible surface) and after a proposal is accepted or rejected, which removes
 * the focused card from the DOM (Track 3 T3-4).
 */

import { useEffect, useRef, useId } from "react";
import { Loader2, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { ArtifactType } from "@/hooks/useArtifacts";
import { ArtifactTagBar } from "./ArtifactTagBar";

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  tags: ArtifactType[];
  onToggleTag: (artifactType: ArtifactType) => void;
  autoTagged?: ArtifactType[];
  /** Forwarded to `ArtifactTagBar` — `undefined` means "not yet known → enable all" (§4.4). */
  available?: readonly ArtifactType[];
  /** Forwarded into the tag bar's trailing slot. Item 7 puts the Sources button here. */
  trailing?: React.ReactNode;
  isBusy: boolean;
  /**
   * The viewer may read the chat but not post to it (not a member of the project's team). The
   * textarea and Send are disabled and the hint says why; the trailing slot stays usable.
   */
  readOnly?: boolean;
  /** Increment to move DOM focus into the textarea. */
  focusToken?: number;
  className?: string;
}

export function ChatComposer({
  value,
  onChange,
  onSend,
  tags,
  onToggleTag,
  autoTagged,
  available,
  trailing,
  isBusy,
  readOnly = false,
  focusToken = 0,
  className,
}: ChatComposerProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const appliedFocusToken = useRef<number>(focusToken);

  useEffect(() => {
    if (focusToken === appliedFocusToken.current) return;
    appliedFocusToken.current = focusToken;
    textareaRef.current?.focus();
  }, [focusToken]);

  const canSend = value.trim().length > 0 && !isBusy && !readOnly;
  // Unique per instance: the full-page and docked composers can be mounted at once.
  const readOnlyHintId = useId();

  return (
    <div
      className={cn(
        "flex flex-col gap-2 border-t border-outlines bg-white px-4 py-3",
        className,
      )}
    >
      <ArtifactTagBar
        selected={tags}
        onToggle={onToggleTag}
        disabled={isBusy || readOnly}
        autoTagged={autoTagged}
        available={available}
        trailing={trailing}
      />

      <div className="flex items-end gap-2">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (canSend) onSend();
            }
          }}
          placeholder={
            // 🚨 THIS COPY MUST NOT PROMISE QUESTION-ANSWERING. IT DOES NOT WORK.
            //
            // The placeholder used to read "Ask a question, or tag files above…". It was wrong:
            // `postTurn` runs `preflight` over the tagged array, and for an EMPTY array that
            // returns `[]`, so `editable.length === 0` and NO AGENT ROW IS CREATED. The handler
            // then returns 202 Accepted with a null `agent_message_id` and enqueues nothing. The
            // server reports success for a request it will never act on, and `MessageRow`'s
            // `isUnanswerableUntagged` notice apologises for it after the fact.
            //
            // Item 5 of the 2026-10-03 P0 fix batch (§6.6, AD-11) DOES change this — but only
            // once `runAnswerTurn` and the `mode: 'answer'` queue variant actually ship in the
            // backend. They have NOT shipped: there is no `runAnswerTurn` in
            // `packages/projects/services/`, and `post-project-chat-message.ts` step 7 still
            // returns early for an untagged turn. Until that lands, inviting a question here
            // produces a worse defect than the one Item 5 set out to fix — the user takes the
            // invitation, is silently ignored, and reads apology copy that contradicts the
            // invitation they just accepted.
            //
            // WHEN THE BACKEND Q&A PATH SHIPS: flip this to §6.6's wording, flip the hint line
            // below, flip `ChatTranscript`'s empty state, DELETE `MessageRow`'s
            // `isUnanswerableUntagged` block, and delete these three comments — leaving them would
            // tell the next reader that Q&A does not exist. All five move together or none do.
            tags.length === 0
              ? "Tag the files to change above, then describe the change…"
              : `Describe the change to ${tags.length} file${tags.length > 1 ? "s" : ""}…`
          }
          aria-label="Message to the project chat"
          aria-describedby={readOnly ? readOnlyHintId : undefined}
          disabled={isBusy || readOnly}
          className="max-h-40 min-h-[44px] flex-1 resize-none"
          rows={1}
        />
        <Button
          size="icon"
          onClick={onSend}
          disabled={!canSend}
          aria-label="Send message"
        >
          {isBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Send className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
      </div>

      <p
        className="text-xs text-text-secondary"
        id={readOnly ? readOnlyHintId : undefined}
      >
        {readOnly
          ? "Only members of this project's team can send changes to the project chat."
          : tags.length === 0
            ? // Same constraint as the placeholder above, stated as a limitation rather than an
              // instruction. It is the only surface that tells the user WHY Send will appear to do
              // nothing, so it must stay until `runAnswerTurn` ships. See the placeholder comment.
              "Enter to send · tag at least one file above — it does not answer untagged questions"
            : tags.length > 1
              ? "Tagging more than one file runs a cross-file consistency pass before drafting."
              : "Enter to send · Shift+Enter for a new line"}
      </p>
    </div>
  );
}

export default ChatComposer;
